/**
 * Circumference measurement sites (measurement-protocol.md).
 *
 * Required sites feed `male_navy_circumference_v1`. Recommended and optional
 * sites may be **skipped entirely** in a given week, but a site that IS recorded
 * always uses the full three-reading protocol — there is no reduced mode for any
 * site (D-045).
 */

export const MEASUREMENT_SITES = [
  'waistAtNavel',
  'neck',
  'chest',
  'leftUpperArm',
  'rightUpperArm',
  'leftThigh',
  'rightThigh',
  'narrowestWaist',
  'hips',
] as const

export type MeasurementSiteKey = (typeof MEASUREMENT_SITES)[number]

export type SiteCategory = 'required' | 'recommended' | 'optional'

export const SITE_CATEGORY: Readonly<Record<MeasurementSiteKey, SiteCategory>> = {
  waistAtNavel: 'required',
  neck: 'required',
  chest: 'recommended',
  leftUpperArm: 'recommended',
  rightUpperArm: 'recommended',
  leftThigh: 'recommended',
  rightThigh: 'recommended',
  narrowestWaist: 'optional',
  hips: 'optional',
}

/** Display labels. Presentation only — never persisted. */
export const SITE_LABELS: Readonly<Record<MeasurementSiteKey, string>> = {
  waistAtNavel: 'Waist at navel',
  neck: 'Neck',
  chest: 'Chest',
  leftUpperArm: 'Left upper arm',
  rightUpperArm: 'Right upper arm',
  leftThigh: 'Left thigh',
  rightThigh: 'Right thigh',
  narrowestWaist: 'Narrowest waist',
  hips: 'Hips',
}

export const REQUIRED_SITES: readonly MeasurementSiteKey[] = MEASUREMENT_SITES.filter(
  (site) => SITE_CATEGORY[site] === 'required',
)

export function isMeasurementSite(value: string): value is MeasurementSiteKey {
  return (MEASUREMENT_SITES as readonly string[]).includes(value)
}

/**
 * The narrowest-waist measurement must never substitute for waist-at-navel in
 * the formula (measurement-protocol.md). Keeping them as distinct sites makes
 * that structural rather than a convention.
 */
export const NAVY_FORMULA_SITES = {
  waist: 'waistAtNavel',
  neck: 'neck',
} as const satisfies Record<string, MeasurementSiteKey>
